import {DEMO_LINK} from 'utils/getters'
import {HeroSectionDataType} from './contentTypes'
import heroImg from 'assets/icons/people-management/hero.webp'
import blueTickImg from 'assets/icons/common/blue-tick-circle.svg'

import integrationsImg from 'assets/icons/people-management/integrations.webp'
import securityImg from 'assets/icons/people-management/security.webp'
import reportImg from 'assets/icons/people-management/report.webp'

import profileImg from 'assets/icons/people-management/profile.webp'
import orgChartImg from 'assets/icons/people-management/org-chart.webp'
import directoryImg from 'assets/icons/people-management/directory.webp'

import onboardingImg from 'assets/icons/people-management/onboarding.webp'
import attendanceImg from 'assets/icons/people-management/attendance.webp'
import documentImg from 'assets/icons/people-management/document.webp'
import workflowImg from 'assets/icons/people-management/workflow.webp'
import offboardingImg from 'assets/icons/people-management/offboarding.webp'

import {ISectionWithCard} from 'types/component-content-type'

export const heroSectionData: HeroSectionDataType = {
  topTitle: 'PEOPLE MANAGEMENT',
  title: 'A powerful platform to manage & automate your HR processes',
  desc: 'Efficiently manage your global workforce by putting all the core HR aspects on autopilot – from employee data management to leave & attendance, onboarding, and offboarding.',
  cta: {
    text: 'Schedule a demo',
    link: DEMO_LINK,
  },
  imgSrc: heroImg.src,
}

export const employeeDatabaseData: ISectionWithCard = {
  topTitle: 'EMPLOYEE DATABASE',
  title: 'A single source of truth for employee data',
  desc: `Keeping track of employee information spread across spreadsheets, individual documents, and multiple HR systems is a daunting task. ZenAdmin makes accessing, updating and verifying employee records possible within seconds, giving you the confidence that your data is always accurate and up-to-date.`,
  cards: [
    {
      title: 'Employee Profiles',
      desc: 'Manage employee profiles with details including role, department, accesses, documents, IDs, personal info, compensation, time off data etc. in one place.',
      style: {
        background: 'white',
        border: '1px solid rgba(37, 77, 218, 0.10)',
        boxShadow: '6px 2px 43px 0px rgba(0, 0, 0, 0.05)',
      },
      img: {
        src: profileImg,
        width: 240,
        height: 245,
        alt: 'employee-profile',
      },
    },
    {
      title: 'Employee Directory',
      desc: 'Improve workforce visibility by easily filter-viewing employees based on roles, department, status, country and more.',
      style: {
        background: 'white',
        border: '1px solid rgba(37, 77, 218, 0.10)',
        boxShadow: '6px 2px 43px 0px rgba(0, 0, 0, 0.05)',
      },

      img: {
        src: directoryImg,
        width: 366,
        height: 178,
        alt: 'employee-directory',
      },
    },
    {
      title: 'Org Charts',
      desc: 'Get an overview of your organisational structure and improve inter-departmental collaboration and understanding.',
      style: {
        background: 'white',
        border: '1px solid rgba(37, 77, 218, 0.10)',
        boxShadow: '6px 2px 43px 0px rgba(0, 0, 0, 0.05)',
      },
      img: {
        src: orgChartImg,
        width: 290,
        height: 255,
        alt: 'org-chart',
      },
    },
  ],
  id: 'lifecycle',
}

export const deviceOffBoardingData = {
  topTitle: 'UNIFIED HR SYSTEM',
  title: 'Reduce manual work by putting your HR processes on autopilot',
  // desc: 'Keep your devices and data protected during offboarding. ZenAdmin makes the entire offboarding process hassle-free by automating it.',
  slides: [
    {
      btnDetails: {
        text: 'Onboarding',
        desc: 'Onboard new employees in minutes – 100% remotely',
      },
      slideDetails: {
        imgSrc: onboardingImg,
        imgBg: 'rgba(37, 77, 218, 0.14)',
        background: '',
        topTitle: '',
        title: 'Onboard new employees in minutes – 100% remotely',
        desc: 'Equip your new hires with everything they need to be productive from day 1. With ZenAdmin, you can ensure a smooth onboarding with our customizable workflows.',
        tags: ['Device Procurement & Setup', 'Customizable Workflows', 'Zero Touch Deployment'],
      },
    },
    {
      btnDetails: {
        text: 'Time & Attendance',
        desc: `Manage & track employee time off and attendance`,
      },
      slideDetails: {
        imgSrc: attendanceImg,
        imgBg: 'rgba(37, 77, 218, 0.25)',
        background: '',
        topTitle: '',
        title: 'Manage & track employee time off and attendance',
        desc: 'Accurately track employee time and reduce downtime. Approve leave requests, and manage time away from the ZenAdmin’s dashboard.',
        tags: ['Custom PTO policies', 'Time Off Requests & Approvals', ' Automatic Notification'],
      },
    },
    {
      btnDetails: {
        text: 'Document Management',
        desc: 'Manage employee documents without hassle',
      },
      slideDetails: {
        imgSrc: documentImg,
        imgBg: 'rgba(37, 77, 218, 0.25)',
        background: '',
        topTitle: '',
        title: 'Manage employee documents without hassle',
        desc: 'Seamlessly manage and update employee records (personal, medical, financial and government documents) in one place.',
        tags: [
          'Easy Audit & Update',
          'Regulate Edit Accesses',
          'Unified System for Document Management',
        ],
      },
    },
    {
      btnDetails: {
        text: 'Workflows',
        desc: 'Boost your team’s efficiency with custom workflows',
      },
      slideDetails: {
        imgSrc: workflowImg,
        imgBg: 'rgba(37, 77, 218, 0.25)',
        background: '',
        topTitle: '',
        title: 'Boost your team’s efficiency with custom workflows',
        desc: 'Automate HR processes such as sending notifications, setting up meetings, requesting approvals, and assigning tasks with our workflow recipes. Use the templates as is or customize them to fit your requirements.',
        tags: [
          'Cross-application Integration (Slack & Google Workspace)',
          'Automated Approvals Processes',
          'No-code Workflows',
        ],
      },
    },
    {
      btnDetails: {
        text: 'Offboarding',
        desc: 'Ensure a secure employee offboarding with ZenAdmin',
      },
      slideDetails: {
        imgSrc: offboardingImg,
        imgBg: 'rgba(37, 77, 218, 0.25)',
        background: '',
        topTitle: '',
        title: 'Ensure a secure employee offboarding with ZenAdmin',
        desc: 'With ZenAdmin you can trigger a surge of HR and IT actions, automating your offboarding process end-to-end. We ensure your devices are safely retrieved and the data is secured when an employee exits.',
        tags: [
          'Remote Device Lock Out & Wiping',
          'Work Account Deactivation',
          'Global Device Retrieval',
        ],
      },
    },
  ],
}
export const reportingData = {
  topTitle: 'REPORTING & ANALYTICS',
  title: 'Gain full insight into company trends',
  desc: 'View workforce trends on headcount, salary, retention, time off etc. on the analytics dashboard. Make data-driven decisions to reduce downtime and boost productivity.',
  infoBoxes: [
    {
      title: 'Customizable reports',
      icon: blueTickImg,
    },
    {
      title: 'Easy to share & collaborate',
      icon: blueTickImg,
    },
    {
      title: 'Centralized view of HR trends',
      icon: blueTickImg,
    },
    {
      title: 'Leave & Attendance insights',
      icon: blueTickImg,
    },
  ],
  imageSrc: reportImg,
  id: 'employee-directory',
}

export const integrationsData = {
  topTitle: 'SEAMLESS INTEGRATIONS',
  title: 'Use ours or synchronise with your existing HR systems',
  desc: 'ZenAdmin integrates with HR systems like BambooHR, Personio, Hibob etc. allowing you to single-handedly navigate through employee data across various systems and keep your data always synced and error-free.',
  infoBoxes: [
    {
      title: 'Synchronised employee data',
      icon: blueTickImg,
    },
    {
      title: 'Improved transparency',
      icon: blueTickImg,
    },
    {
      title: 'Minimal IT intervention',
      icon: blueTickImg,
    },
  ],
  imageSrc: integrationsImg,
  id: 'employee-directory',
}

export const securityData = {
  topTitle: 'COMPLIANCE & SECURITY',
  title: 'Keep your cloud-based resources and data secured',
  desc: 'We follow the highest data protection and security standards. Also, you can maintain control over who has access to your cloud-based resources and data.',
  infoBoxes: [
    {
      title: 'Implementation of security best practices',
      icon: blueTickImg,
    },
    {
      title: 'Automated onboarding & offboarding',
      icon: blueTickImg,
    },
    {
      title: 'Controlled data access',
      icon: blueTickImg,
    },
  ],
  imageSrc: securityImg,
  id: 'employee-directory',
}
